import { type ReactNode, useId } from "react";
import { styled } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { Chip } from "./Chip";
import { useChipSelectContext } from "./ChipSelectContext";
import CheckIcon from "@mui/icons-material/Check";

export interface ChipSelectOptionProps {
  value: string;
  badge?: ReactNode;
  disabled?: boolean;
  children: ReactNode;
}

const HiddenInput = styled("input")({
  ...visuallyHidden,
});

const StyledChip = styled(Chip)({
  cursor: "pointer",
});

export const ChipSelectOption = ({
  value,
  badge,
  disabled: optionDisabled = false,
  children,
}: ChipSelectOptionProps) => {
  const { value: selectedValue, multiple, name, disabled: groupDisabled, onChange } = useChipSelectContext();
  const id = useId();

  const isDisabled = groupDisabled || optionDisabled;

  const isSelected = multiple ? Array.isArray(selectedValue) && selectedValue.includes(value) : selectedValue === value;

  const inputType = multiple ? "checkbox" : "radio";

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isDisabled) {
      onChange(event, value);
    }
  };

  const handleChipClick = () => {
    if (!isDisabled) {
      const syntheticEvent = {
        target: { value, checked: !isSelected },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent, value);
    }
  };

  const label =
    badge !== undefined ? (
      <>
        {children}
        <span style={{ marginLeft: 4 }}>{badge}</span>
      </>
    ) : (
      children
    );

  return (
    <>
      <HiddenInput
        type={inputType}
        id={id}
        name={name}
        value={value}
        checked={isSelected}
        disabled={isDisabled}
        onChange={handleChange}
        aria-label={typeof children === "string" ? children : undefined}
      />
      <StyledChip
        variant="filter"
        icon={isSelected ? <CheckIcon /> : undefined}
        selected={isSelected}
        disabled={isDisabled}
        label={label}
        onClick={handleChipClick}
      />
    </>
  );
};
